import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { profileImageFileSize, profileImageSpec } from 'fym-shared/src/music24/artistProfile';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { SpotifyHeaderFrame } from '../../studio/spotifyHeader/SpotifyHeaderFrame';
import type { HeaderPreviewGround } from '../../studio/spotifyHeader/FrameGround';
import { ProfileImageFrame } from '../frame/ProfileImageFrame';
import { profileDownloadUrl, profileFileGround, profileFileUrl } from '../profileAssets';
import { spotifyCardChip } from '../profileChips';
import { profileSlotOf } from '../profileSlots';
import { profileMessages as m } from '../profileMessages';
import { spotifyButtonMode, spotifyImageState } from '../spotify/spotifyUploadState';
import { ProfileCard } from './ProfileCard';
import { ProfileChipBadge } from './ProfileChipBadge';
import { ProfileSlotNote } from './ProfileSlotNote';
import { ProfileUpgradeButton } from './ProfileUpgradeButton';
import { ProfileDownloadLink } from './ProfileDownloadLink';
import type { ProfileCardProps } from './profileCardProps';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Profile; each part uses its own slice of it */
import styles from '../Profile.module.scss';
/* eslint-enable css-modules/no-unused-class */

const AVATAR_SPEC = profileImageSpec('spotifyAvatar');

/**
 * The Spotify card (design a1, a2, d1, d4): the header with the name and the photo where Spotify
 * draws them, the round artist photo, Edit header, Edit photo, Download and the Spotify button
 * (Upload, or "Upgrade to send to Spotify" on Free; none with no Auto upload access).
 */
const ProfileSpotifyCardComponent: React.FC<{ card: ProfileCardProps }> = ({ card }) => {
  const { profile, colours, artistName, avatarUrl, narrow, openTool, openSheet, retry, isMaking } = card;
  const intl = useIntl();
  const header = useMemo(() => profileSlotOf(profile.assets, 'spotifyHeader'), [profile.assets]);
  const avatar = useMemo(() => profileSlotOf(profile.assets, 'spotifyAvatar'), [profile.assets]);
  const headerGround: HeaderPreviewGround = profileFileGround(header.current) ?? { kind: 'theme' };
  const avatarFileUrl = profileFileUrl(avatar.current);
  // No photo version yet: the Music24 artist photo of Settings → Artist (design a2 note).
  const shownAvatar = avatarFileUrl ?? avatarUrl;
  const avatarGround: HeaderPreviewGround =
    shownAvatar === null ? { kind: 'theme' } : { kind: 'picture', url: shownAvatar, focus: { x: 0.5, y: 0.5 } };
  const mode = spotifyButtonMode(profile);
  const headerUpload = spotifyImageState(header.current);
  const download = profileDownloadUrl(header.current);
  const headerWidth = narrow ? Math.min(334, window.innerWidth - 56) : 560;
  const preview = profileImageFileSize(profileImageSpec('spotifyHeader'), true);

  let meta: string | null = null;
  if (headerUpload.kind === 'onSpotify' && header.current !== null) {
    meta = intl.formatMessage(m.spotifyOnSince, {
      date: intl.formatDate(headerUpload.at, { day: 'numeric', month: 'short' }),
      version: header.current.version,
    });
  } else if (avatar.current === null && avatarUrl !== null) {
    meta = intl.formatMessage(m.spotifyPhotoFromSettings);
  }

  let size = intl.formatMessage(narrow ? m.spotifySizeNarrow : m.spotifySize);
  if (profile.preview && header.current !== null) {
    size = intl.formatMessage(m.spotifyPreviewSize, preview);
  }

  return (
    <ProfileCard
      service="spotify"
      name={intl.formatMessage(m.spotifyName)}
      size={size}
      chip={<ProfileChipBadge chip={spotifyCardChip(header, avatar)} testId="profile-spotify-chip" />}
      wide
      testId="profile-card-spotify"
    >
      <div className={`${styles.stage} ${styles.stageStart}`}>
        <SpotifyHeaderFrame
          width={headerWidth}
          view="file"
          ground={headerGround}
          colours={colours}
          overlay={{ artist: artistName, avatarUrl: shownAvatar }}
          testId="profile-spotify-header"
        />
        {!narrow && (
          <div className={styles.stageCol}>
            <ProfileImageFrame
              spec={AVATAR_SPEC}
              width={150}
              ground={avatarGround}
              colours={colours}
              round
              testId="profile-spotify-avatar"
            />
            <span className={styles.meta}>{intl.formatMessage(m.artistPhoto)}</span>
          </div>
        )}
      </div>
      <ProfileSlotNote
        slots={[header, avatar]}
        name={intl.formatMessage(m.spotifyName)}
        onTryAgain={() => retry([header, avatar])}
        isBusy={isMaking}
      />
      <div className={styles.row}>
        <SMLButton className={styles.sm} onClick={() => openTool('spotifyHeader')}>
          {intl.formatMessage(narrow ? m.edit : m.editHeader)}
        </SMLButton>
        {!narrow && (
          <SMLButton className={styles.sm} onClick={() => openTool('spotifyAvatar')}>
            {intl.formatMessage(m.editPhoto)}
          </SMLButton>
        )}
        {download !== null && !narrow && <ProfileDownloadLink href={download} testId="profile-spotify-download" />}
        {mode === 'send' && (
          <SMLButton className={styles.sm} onClick={openSheet}>
            <span className={styles.spotDot} aria-hidden="true" />
            {intl.formatMessage(narrow ? m.uploadShort : m.uploadToSpotify)}
          </SMLButton>
        )}
        {mode === 'upgrade' && <ProfileUpgradeButton label={m.upgradeToSend} small />}
        {mode === 'manual' && (header.current !== null || avatar.current !== null) && (
          <button type="button" className={styles.link} onClick={openSheet} data-testid="profile-spotify-howto">
            {intl.formatMessage(m.putItUpYourself)}
          </button>
        )}
        <span className={styles.grow} />
        {meta !== null && !narrow && <span className={styles.meta}>{meta}</span>}
      </div>
    </ProfileCard>
  );
};

export const ProfileSpotifyCard = TypedMemo(ProfileSpotifyCardComponent);
